import * as THREE from 'three';

interface Particle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
  size: number;
  color: THREE.Color;
}

export class ParticleSystem {
  private particles: Particle[] = [];
  private geometry: THREE.BufferGeometry;
  private material: THREE.PointsMaterial;
  private points: THREE.Points;
  private maxParticles: number;

  private posArray: Float32Array;
  private colorArray: Float32Array;
  private sizeArray: Float32Array;

  constructor(scene: THREE.Scene, maxParticles: number = 5000) {
    this.maxParticles = maxParticles;

    this.posArray = new Float32Array(maxParticles * 3);
    this.colorArray = new Float32Array(maxParticles * 3);
    this.sizeArray = new Float32Array(maxParticles);

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.posArray, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colorArray, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(this.sizeArray, 1));

    // Simple circle sprite texture
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.4, 'rgba(255,255,255,0.8)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 32, 32);

    const spriteTexture = new THREE.CanvasTexture(canvas);

    this.material = new THREE.PointsMaterial({
      size: 1,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      map: spriteTexture,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    scene.add(this.points);
  }

  /**
   * Emit an explosion burst at a position.
   */
  public emitExplosion(position: THREE.Vector3, radius: number, count: number = 60): void {
    for (let i = 0; i < count && this.particles.length < this.maxParticles; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = (2 + Math.random() * 8) * (radius / 5);

      const vel = new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.cos(phi) * speed * 0.6 + Math.random() * speed * 0.5,
        Math.sin(phi) * Math.sin(theta) * speed
      );

      const t = Math.random();
      const color = new THREE.Color().lerpColors(
        new THREE.Color(1, 0.9, 0.2),    // yellow
        new THREE.Color(1, 0.3, 0.05),  // orange-red
        t
      );

      this.particles.push({
        position: position.clone().add(new THREE.Vector3(
          (Math.random() - 0.5) * radius * 0.3,
          Math.random() * radius * 0.2,
          (Math.random() - 0.5) * radius * 0.3
        )),
        velocity: vel,
        life: 0,
        maxLife: 0.5 + Math.random() * 1.0,
        size: 0.5 + Math.random() * 1.5 * (radius / 5),
        color,
      });
    }
  }

  /**
   * Emit a smaller muzzle flash at a position.
   */
  public emitMuzzleFlash(position: THREE.Vector3, direction: THREE.Vector3): void {
    for (let i = 0; i < 5 && this.particles.length < this.maxParticles; i++) {
      const spread = 0.3;
      const vel = direction.clone().multiplyScalar(8 + Math.random() * 5).add(
        new THREE.Vector3(
          (Math.random() - 0.5) * spread,
          (Math.random() - 0.5) * spread,
          (Math.random() - 0.5) * spread
        )
      );

      this.particles.push({
        position: position.clone(),
        velocity: vel,
        life: 0,
        maxLife: 0.08 + Math.random() * 0.1,
        size: 0.3 + Math.random() * 0.5,
        color: new THREE.Color(1, 0.95, 0.6),
      });
    }
  }

  /**
   * Emit a trail puff at a position (for projectile smoke trail).
   */
  public emitTrail(position: THREE.Vector3, color: THREE.Color = new THREE.Color(0.8, 0.8, 0.8)): void {
    if (this.particles.length >= this.maxParticles) return;

    this.particles.push({
      position: position.clone(),
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 0.5,
        0.5 + Math.random() * 0.5,
        (Math.random() - 0.5) * 0.5
      ),
      life: 0,
      maxLife: 0.8 + Math.random() * 0.5,
      size: 0.3 + Math.random() * 0.4,
      color: color.clone(),
    });
  }

  /**
   * Emit bazooka smoke trail (thicker, lingers longer).
   */
  public emitBazookaTrail(position: THREE.Vector3): void {
    for (let i = 0; i < 3 && this.particles.length < this.maxParticles; i++) {
      this.particles.push({
        position: position.clone().add(new THREE.Vector3(
          (Math.random() - 0.5) * 0.3,
          (Math.random() - 0.5) * 0.3,
          (Math.random() - 0.5) * 0.3
        )),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.8,
          0.3 + Math.random() * 0.5,
          (Math.random() - 0.5) * 0.8
        ),
        life: 0,
        maxLife: 1.5 + Math.random() * 0.5,
        size: 0.6 + Math.random() * 0.8,
        color: new THREE.Color(0.6, 0.6, 0.6),
      });
    }
  }

  /**
   * Emit dust puff (terrain impact).
   */
  public emitDust(position: THREE.Vector3, count: number = 20): void {
    for (let i = 0; i < count && this.particles.length < this.maxParticles; i++) {
      const theta = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 3;

      this.particles.push({
        position: position.clone(),
        velocity: new THREE.Vector3(
          Math.cos(theta) * speed,
          1 + Math.random() * 3,
          Math.sin(theta) * speed
        ),
        life: 0,
        maxLife: 0.8 + Math.random() * 0.8,
        size: 0.4 + Math.random() * 0.6,
        color: new THREE.Color(0.6, 0.5, 0.35),
      });
    }
  }

  /**
   * Emit napalm fire particles.
   */
  public emitNapalm(position: THREE.Vector3, radius: number): void {
    for (let i = 0; i < 8 && this.particles.length < this.maxParticles; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius;
      const pos = position.clone().add(new THREE.Vector3(
        Math.cos(angle) * dist,
        Math.random() * 0.5,
        Math.sin(angle) * dist
      ));

      this.particles.push({
        position: pos,
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.5,
          1 + Math.random() * 2,
          (Math.random() - 0.5) * 0.5
        ),
        life: 0,
        maxLife: 0.3 + Math.random() * 0.5,
        size: 0.5 + Math.random() * 1,
        color: new THREE.Color().lerpColors(
          new THREE.Color(1, 0.8, 0),
          new THREE.Color(1, 0.2, 0),
          Math.random()
        ),
      });
    }
  }

  // ─── Weapon-Specific Trail Effects ───

  /**
   * Fire trail (napalm, homing) — orange/red flaming particles.
   */
  public emitFireTrail(position: THREE.Vector3): void {
    for (let i = 0; i < 2 && this.particles.length < this.maxParticles; i++) {
      this.particles.push({
        position: position.clone().add(new THREE.Vector3(
          (Math.random() - 0.5) * 0.2,
          (Math.random() - 0.5) * 0.2,
          (Math.random() - 0.5) * 0.2
        )),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.6,
          0.5 + Math.random() * 1.0,
          (Math.random() - 0.5) * 0.6
        ),
        life: 0,
        maxLife: 0.4 + Math.random() * 0.4,
        size: 0.3 + Math.random() * 0.5,
        color: new THREE.Color().lerpColors(
          new THREE.Color(1, 0.6, 0),
          new THREE.Color(1, 0.15, 0),
          Math.random()
        ),
      });
    }
  }

  /**
   * Heavy smoke trail (nuke) — dense dark smoke with large particles.
   */
  public emitHeavySmokeTrail(position: THREE.Vector3): void {
    for (let i = 0; i < 4 && this.particles.length < this.maxParticles; i++) {
      this.particles.push({
        position: position.clone().add(new THREE.Vector3(
          (Math.random() - 0.5) * 0.4,
          (Math.random() - 0.5) * 0.4,
          (Math.random() - 0.5) * 0.4
        )),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 1.0,
          0.3 + Math.random() * 0.8,
          (Math.random() - 0.5) * 1.0
        ),
        life: 0,
        maxLife: 1.8 + Math.random() * 0.7,
        size: 0.8 + Math.random() * 1.2,
        color: new THREE.Color().lerpColors(
          new THREE.Color(0.4, 0.1, 0.0),
          new THREE.Color(0.2, 0.2, 0.2),
          Math.random()
        ),
      });
    }
  }

  /**
   * Spark trail (shockwave, sniper, shotgun) — tiny bright fast-fading sparks.
   */
  public emitSparkTrail(position: THREE.Vector3, color: THREE.Color): void {
    if (this.particles.length >= this.maxParticles) return;
    this.particles.push({
      position: position.clone(),
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2
      ),
      life: 0,
      maxLife: 0.15 + Math.random() * 0.2,
      size: 0.15 + Math.random() * 0.2,
      color: color.clone(),
    });
  }

  /**
   * Rocket exhaust trail (homing missile) — like bazooka but red-tinted.
   */
  public emitRocketTrail(position: THREE.Vector3): void {
    for (let i = 0; i < 3 && this.particles.length < this.maxParticles; i++) {
      this.particles.push({
        position: position.clone().add(new THREE.Vector3(
          (Math.random() - 0.5) * 0.3,
          (Math.random() - 0.5) * 0.3,
          (Math.random() - 0.5) * 0.3
        )),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.8,
          0.3 + Math.random() * 0.5,
          (Math.random() - 0.5) * 0.8
        ),
        life: 0,
        maxLife: 1.0 + Math.random() * 0.5,
        size: 0.5 + Math.random() * 0.6,
        color: new THREE.Color().lerpColors(
          new THREE.Color(1, 0.4, 0.1),
          new THREE.Color(0.5, 0.5, 0.5),
          Math.random()
        ),
      });
    }
  }

  /**
   * Dirt/ground trail (roller, digger, dirt mover) — brown dust kicked up.
   */
  public emitDirtTrail(position: THREE.Vector3): void {
    if (this.particles.length >= this.maxParticles) return;
    this.particles.push({
      position: position.clone(),
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 1.5,
        0.5 + Math.random() * 1.0,
        (Math.random() - 0.5) * 1.5
      ),
      life: 0,
      maxLife: 0.5 + Math.random() * 0.5,
      size: 0.3 + Math.random() * 0.4,
      color: new THREE.Color(0.5 + Math.random() * 0.15, 0.4 + Math.random() * 0.1, 0.25),
    });
  }

  /**
   * Sparkle trail (MIRVs) — bright yellow/white twinkle.
   */
  public emitSparkleTrail(position: THREE.Vector3): void {
    if (this.particles.length >= this.maxParticles) return;
    this.particles.push({
      position: position.clone(),
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 1.0,
        (Math.random() - 0.5) * 1.0,
        (Math.random() - 0.5) * 1.0
      ),
      life: 0,
      maxLife: 0.2 + Math.random() * 0.3,
      size: 0.2 + Math.random() * 0.3,
      color: new THREE.Color().lerpColors(
        new THREE.Color(1, 1, 0.5),
        new THREE.Color(1, 1, 1),
        Math.random()
      ),
    });
  }

  // ─── Weapon-Specific Explosion Effects ───

  /**
   * Nuke explosion — massive white flash → mushroom cloud.
   */
  public emitNukeExplosion(position: THREE.Vector3, radius: number): void {
    // White flash burst
    for (let i = 0; i < 80 && this.particles.length < this.maxParticles; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = (3 + Math.random() * 10) * (radius / 5);

      this.particles.push({
        position: position.clone(),
        velocity: new THREE.Vector3(
          Math.sin(phi) * Math.cos(theta) * speed,
          Math.cos(phi) * speed * 0.5 + Math.random() * speed,
          Math.sin(phi) * Math.sin(theta) * speed
        ),
        life: 0,
        maxLife: 0.3 + Math.random() * 0.5,
        size: 1.5 + Math.random() * 3.0 * (radius / 10),
        color: new THREE.Color(1, 1, 0.9),
      });
    }
    // Orange fireball
    for (let i = 0; i < 100 && this.particles.length < this.maxParticles; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = (2 + Math.random() * 6) * (radius / 5);

      this.particles.push({
        position: position.clone().add(new THREE.Vector3(
          (Math.random() - 0.5) * radius * 0.5,
          Math.random() * radius * 0.3,
          (Math.random() - 0.5) * radius * 0.5
        )),
        velocity: new THREE.Vector3(
          Math.sin(phi) * Math.cos(theta) * speed * 0.5,
          Math.abs(Math.cos(phi)) * speed + 3,
          Math.sin(phi) * Math.sin(theta) * speed * 0.5
        ),
        life: 0,
        maxLife: 1.0 + Math.random() * 1.5,
        size: 1.0 + Math.random() * 2.5 * (radius / 10),
        color: new THREE.Color().lerpColors(
          new THREE.Color(1, 0.5, 0),
          new THREE.Color(0.3, 0.1, 0),
          Math.random()
        ),
      });
    }
    // Dark mushroom cloud top
    for (let i = 0; i < 60 && this.particles.length < this.maxParticles; i++) {
      const theta = Math.random() * Math.PI * 2;
      const spread = Math.random() * radius * 0.3;

      this.particles.push({
        position: position.clone().add(new THREE.Vector3(0, radius * 0.5, 0)),
        velocity: new THREE.Vector3(
          Math.cos(theta) * spread * 0.3,
          2 + Math.random() * 4,
          Math.sin(theta) * spread * 0.3
        ),
        life: 0,
        maxLife: 2.0 + Math.random() * 2.0,
        size: 1.5 + Math.random() * 3.0 * (radius / 10),
        color: new THREE.Color(0.15 + Math.random() * 0.1, 0.1, 0.05),
      });
    }
  }

  /**
   * Shockwave ring explosion — blue particles expanding outward in a ring.
   */
  public emitShockwaveExplosion(position: THREE.Vector3, radius: number): void {
    const ringCount = 50;
    for (let i = 0; i < ringCount && this.particles.length < this.maxParticles; i++) {
      const angle = (i / ringCount) * Math.PI * 2;
      const speed = (5 + Math.random() * 5) * (radius / 8);

      this.particles.push({
        position: position.clone(),
        velocity: new THREE.Vector3(
          Math.cos(angle) * speed,
          0.5 + Math.random() * 1.5,
          Math.sin(angle) * speed
        ),
        life: 0,
        maxLife: 0.6 + Math.random() * 0.6,
        size: 0.6 + Math.random() * 1.0 * (radius / 8),
        color: new THREE.Color().lerpColors(
          new THREE.Color(0.3, 0.7, 1),
          new THREE.Color(0.8, 0.9, 1),
          Math.random()
        ),
      });
    }
    // Central burst
    this.emitExplosion(position, radius * 0.4, 20);
  }

  /**
   * Bouncer impact sparks — magenta sparks at bounce point.
   */
  public emitBounceSparks(position: THREE.Vector3): void {
    for (let i = 0; i < 10 && this.particles.length < this.maxParticles; i++) {
      const theta = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 4;

      this.particles.push({
        position: position.clone(),
        velocity: new THREE.Vector3(
          Math.cos(theta) * speed,
          2 + Math.random() * 3,
          Math.sin(theta) * speed
        ),
        life: 0,
        maxLife: 0.2 + Math.random() * 0.3,
        size: 0.2 + Math.random() * 0.3,
        color: new THREE.Color().lerpColors(
          new THREE.Color(1, 0.3, 1),
          new THREE.Color(1, 0.6, 1),
          Math.random()
        ),
      });
    }
  }

  /**
   * Update all particles and sync to GPU buffers.
   */
  public update(dt: number): void {
    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life += dt;

      if (p.life >= p.maxLife) {
        this.particles.splice(i, 1);
        continue;
      }

      // Apply velocity with drag
      p.position.addScaledVector(p.velocity, dt);
      p.velocity.y -= 3 * dt; // light gravity
      p.velocity.multiplyScalar(0.98); // drag
    }

    // Sync to buffers
    const posAttr = this.geometry.attributes.position as THREE.BufferAttribute;
    const colAttr = this.geometry.attributes.color as THREE.BufferAttribute;
    const sizeAttr = this.geometry.attributes.size as THREE.BufferAttribute;

    for (let i = 0; i < this.maxParticles; i++) {
      if (i < this.particles.length) {
        const p = this.particles[i];
        const fade = 1 - (p.life / p.maxLife);
        posAttr.setXYZ(i, p.position.x, p.position.y, p.position.z);
        colAttr.setXYZ(i, p.color.r * fade, p.color.g * fade, p.color.b * fade);
        sizeAttr.setX(i, p.size * fade);
      } else {
        posAttr.setXYZ(i, 0, -1000, 0); // off-screen
        sizeAttr.setX(i, 0);
      }
    }

    posAttr.needsUpdate = true;
    colAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;

    this.geometry.setDrawRange(0, this.particles.length);
  }

  public getActiveCount(): number {
    return this.particles.length;
  }
}
